import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_STYLES = {
  new: "border-gold/50 text-gold",
  contacted: "border-sky-400/50 text-sky-300",
  closed: "border-zinc-500/50 text-zinc-400",
};

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch {
      navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}/status`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-obsidian px-5 py-10 sm:px-8 lg:px-16">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Nivaan Experiences logo" className="h-9 w-9 rounded-full object-cover" />
            <div>
              <p className="font-serif text-2xl font-medium tracking-[0.35em] text-zinc-100">NIVAAN</p>
              <p className="mt-1 text-[9px] font-mono uppercase tracking-[0.4em] text-gold">Journey Enquiries</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-button" onClick={load} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-zinc-300 hover:border-gold hover:text-gold">
              <RefreshCw size={14} /> Refresh
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-zinc-300 hover:border-red-400 hover:text-red-400">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {enquiries === null ? (
          <div className="mt-24 flex justify-center text-zinc-500">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : enquiries.length === 0 ? (
          <p data-testid="admin-empty-state" className="mt-24 text-center font-serif text-2xl font-light italic text-zinc-500">
            No enquiries yet. They'll appear here as they arrive.
          </p>
        ) : (
          <div className="mt-10 space-y-5">
            {enquiries.map((e) => (
              <div key={e.id} data-testid={`enquiry-card-${e.id}`} className="rounded-sm border border-white/10 bg-white/[0.03] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-serif text-xl text-zinc-100">{e.name}</h3>
                      <span className={`rounded-full border px-3 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em] ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`}>
                        {e.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs font-light text-zinc-500">
                      {new Date(e.created_at).toLocaleString("en-IN")} — {e.email} — {e.phone}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`enquiry-status-${e.id}`}
                      value={e.status}
                      onChange={(ev) => setStatus(e.id, ev.target.value)}
                      className="rounded-sm border border-white/15 bg-obsidian px-3 py-2 text-xs text-zinc-300 outline-none focus:border-gold"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>
                    <button data-testid={`enquiry-delete-${e.id}`} onClick={() => remove(e.id)} className="rounded-sm border border-white/15 p-2 text-zinc-400 hover:border-red-400 hover:text-red-400" aria-label="Delete enquiry">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm font-light text-zinc-400 sm:grid-cols-3 lg:grid-cols-4">
                  <p><span className="text-zinc-600">Destination: </span>{e.destination || (e.open_to_suggestions ? "Open to suggestions" : "—")}</p>
                  <p><span className="text-zinc-600">Dates: </span>{e.start_date || "—"} → {e.end_date || "—"}</p>
                  <p><span className="text-zinc-600">Travellers: </span>{e.adults} adults, {e.children} children</p>
                  <p><span className="text-zinc-600">Budget: </span>{e.budget || "—"}</p>
                  <p><span className="text-zinc-600">Journey: </span>{e.journey_type || "—"}</p>
                  <p className="col-span-2 sm:col-span-3"><span className="text-zinc-600">Arrange: </span>{(e.arrangements || []).join(", ") || "—"}</p>
                  {e.notes && <p className="col-span-2 sm:col-span-3 lg:col-span-4"><span className="text-zinc-600">Notes: </span>{e.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
